import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Clock, Shield } from 'lucide-react';
import { products } from '../mockData';
import { Button } from '../components/ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const Professional = () => {
  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  
  return (
    <div className="min-h-screen pt-20">
      <section className="relative h-96 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_elerien-tr/artifacts/xn5ghotx_e60%20havaliman%C4%B1.jpg"
            alt={t('professionalTitle')}
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center 70%' }}
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="text-white">
            <h1 className="text-5xl font-bold mb-4">{t('professionalTitle')}</h1>
            <p className="text-xl">{t('professionalSubtitle')}</p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <Award className="w-12 h-12 text-sky-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('warranty24')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Uzun garanti süresi ile içiniz rahat' : language === 'en' ? 'Peace of mind with extended warranty' : language === 'de' ? 'Beruhigt mit verlängerter Garantie' : 'راحة البال مع الضمان الممتد'}</p>
            </div>
            <div className="text-center">
              <Shield className="w-12 h-12 text-sky-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('highInspection')}</h3>
              <p className="text-gray-600">{language === 'tr' ? '%100 tam denetimden geçmiş üretim' : language === 'en' ? '100% fully inspected production' : language === 'de' ? '100% vollständig geprüfte Produktion' : 'إنتاج مفحوص بالكامل 100٪'}</p>
            </div>
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-sky-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('spareParts')}</h3>
              <p className="text-gray-600">{language === 'tr' ? '10 yıl boyunca yedek parça desteği' : language === 'en' ? '10 years of spare parts support' : language === 'de' ? '10 Jahre Ersatzteilunterstützung' : 'دعم قطع الغيار لمدة 10 سنوات'}</p>
            </div>
            <div className="text-center">
              <Clock className="w-12 h-12 text-sky-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('technicalSupport')}</h3>
              <p className="text-gray-600">{language === 'tr' ? 'Service Macro güvencesi ile destek' : language === 'en' ? 'Support with Service Macro guarantee' : language === 'de' ? 'Support mit Service Macro Garantie' : 'دعم بضمان سيرفيس ماكرو'}</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-center mb-12">{t('ourProducts')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.professional.map((product) => (
              <div key={product.id} className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden bg-gray-100">
                  <img
                    src={product.image}
                    alt={t(product.nameKey)}
                    className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-sky-500 font-semibold mb-2">{t('professional')}</div>
                  <h3 className="text-xl font-bold mb-3">{t(product.nameKey)}</h3>
                  <p className="text-gray-600 mb-4">{t(product.descriptionKey)}</p>
                  <div className="flex gap-2">
                    <Link to={product.link} className="flex-1">
                      <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white group">
                        {t('view')}
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </Button>
                    </Link>
                    <Link to={product.catalog} className="flex-1">
                      <Button variant="outline" className="w-full border-sky-500 text-sky-500 hover:bg-sky-50">
                        {t('catalog')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('whyElerein')}</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-sky-500 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-600">{language === 'tr' ? '30 yılı aşkın tecrübesi ile Elerein işinizi geliştirecek.' : language === 'en' ? 'With over 30 years of experience, Elerein will grow your business.' : language === 'de' ? 'Mit über 30 Jahren Erfahrung wird Elerein Ihr Geschäft ausbauen.' : 'مع أكثر من 30 عامًا من الخبرة، ستنمي إليرين عملك.'}</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-sky-500 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-600">{language === 'tr' ? 'ECO modu ile 5 saat boyunca aralıksız çalışma imkanı.' : language === 'en' ? 'ECO mode allows 5 hours of continuous operation.' : language === 'de' ? 'ECO-Modus ermöglicht 5 Stunden Dauerbetrieb.' : 'وضع ECO يتيح 5 ساعات من التشغيل المستمر.'}</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-sky-500 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-600">{language === 'tr' ? 'Hem pil hem de kablo fonksiyonu ile sık sık güç değişimine gerek yok.' : language === 'en' ? 'Both battery and cable function, no frequent power changes needed.' : language === 'de' ? 'Sowohl Batterie- als auch Kabelfunktion, keine häufigen Stromwechsel erforderlich.' : 'كل من وظيفة البطارية والكابل، لا حاجة لتغييرات الطاقة المتكررة.'}</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-sky-500 mr-3 mt-1 flex-shrink-0" />
                  <p className="text-gray-600">{language === 'tr' ? 'Düğmeli ve elektrik panosu tasarımları ile ihtiyacınıza göre seçim yapabilirsiniz.' : language === 'en' ? 'Choose according to your needs with button and electric panel designs.' : language === 'de' ? 'Wählen Sie nach Ihren Bedürfnissen mit Tasten- und Elektro-Panel-Designs.' : 'اختر وفقًا لاحتياجاتك مع تصميمات الأزرار واللوحة الكهربائية.'}</p>
                </div>
              </div>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://customer-assets.emergentagent.com/job_elerien-tr/artifacts/bb2qxhis_Photoroom_20251103_231814.png"
                alt="Elerein Machines"
                className="w-full h-full object-contain bg-gray-100"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{t('productCatalogs')}</h2>
            <p className="text-gray-600 text-lg mb-6">{language === 'tr' ? 'Tüm ürünlerimizin detaylı kataloglarını indirebilirsiniz' : language === 'en' ? 'Download detailed catalogs of all our products' : language === 'de' ? 'Laden Sie detaillierte Kataloge aller unserer Produkte herunter' : 'قم بتنزيل كتالوجات مفصلة لجميع منتجاتنا'}</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { model: 'E1', name: 'E1 Zemin Temizleme Makinesi' },
              { model: 'E60', name: 'E60 Zemin Temizleme Makinesi' },
              { model: 'E100', name: 'E100 endüstriyel sürücülü tip zemin temizleme makinesi' },
              { model: 'E130', name: 'E130 endüstriyel sürücülü tip zemin temizleme makinesi' },
              { model: 'E250', name: 'E250 Endüstriyel Temizlik Makinesi' }
            ].map((item) => (
              <Link
                key={item.model}
                to="/catalog"
                className="group bg-gradient-to-br from-sky-50 to-white border-2 border-teal-100 rounded-lg p-6 hover:shadow-xl hover:border-sky-500 transition-all duration-300"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-lg mb-2 text-gray-900 group-hover:text-sky-500 transition-colors">
                    {item.model}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{item.name}</p>
                  <span className="inline-flex items-center text-sky-500 font-semibold text-sm">
                    {language === 'tr' ? 'Katalogu İndir' : language === 'en' ? 'Download Catalog' : language === 'de' ? 'Katalog herunterladen' : 'تنزيل الكتالوج'}
                    <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block bg-sky-50 rounded-lg p-8">
              <h3 className="text-xl font-bold mb-3">{t('downloadAllCatalogs')}</h3>
              <p className="text-gray-600 mb-4">{language === 'tr' ? 'Tüm ürün kataloglarını görüntüleyin ve indirin' : language === 'en' ? 'View and download all product catalogs' : language === 'de' ? 'Alle Produktkataloge anzeigen und herunterladen' : 'عرض وتنزيل جميع كتالوجات المنتجات'}</p>
              <Link to="/catalog">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-3">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {t('viewCatalog')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Professional;
